import { axios } from '@/_utils/utils';
import Datepicker from '@/components/_common/Datepicker/Datepicker';
import Modal from '@/components/_common/Modal/Modal';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useToast } from '@/components/_common/Toast';
import Tooltip from '@/components/_common/Tooltip';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';
import StepTwo from '@/components/Focus/AvailsSchedule/IncrementalLaunchModal/StepTwo';
import { LaunchModalMode } from '@/components/Focus/AvailsSchedule/IncrementalLaunchModal/types';
import {
  computeLaunchRights,
  getAppliedRights,
  getNewTakedownStartDate,
  getNextQuarterTime,
  getPrevScheduling,
  getTimeMs
} from '@/components/Focus/AvailsSchedule/IncrementalLaunchModal/utils';
import IncrementalLaunchRightsSelectionSection, {
  IncrementalSelectionState,
  HEIGHT as RIGHTS_SELECTION_SECTION_HEIGHT
} from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/IncrementalLaunchRightsSelectionSection';
import ScheduleAvailTypeTabs from '@/components/Focus/AvailsSchedule/ScheduleAvailTypeTabs';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { useRightsAvailable } from '@/components/Focus/hooks/useRightsAvailable';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectFocusRightsPossibleBySchedulingId } from '@/redux/selectors/focusNewScheduleSelectors';
import { getFocusRightsPossibleBySchedulingId } from '@/redux/slices/focusNewScheduleSlice';
import { fetchIncrementalSchedulingsByVersionSchedulingId } from '@/redux/slices/focusScheduleSlice';
import type { SchedulingPostData } from '@/redux/slices/slice_types';
import { FocusScheduling, FocusVersionScheduling, PolarisApiResponse, ScheduleAvailType } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { AxiosResponse } from 'axios';
import { isSameDay } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { defaultAvailEndDate } from '../../constants';

export interface EditIncrementalLaunchModalProps {
  isOpen: boolean;
  versionScheduling: FocusVersionScheduling;
  mode: LaunchModalMode;
  launch?: FocusScheduling; // component supports creating new launches and editing existing ones, so launch is optional
  onClose: () => void;
}

type DateChangeField = 'start' | 'end';

const INITIAL_TAB: ScheduleAvailType = 'audio';
const INITIAL_RIGHTS_STATE: Record<ScheduleAvailType, IncrementalSelectionState> = {
  audio: { add: [], remove: [] },
  timedtext: { add: [], remove: [] },
  territories: { add: [], remove: [] }
};

const DATE_PICKER_TIME_INTERVALS = 15;

// TODO: MINOR THING. We should not allow creating lunches in the past or editing launch start date to be in the past,
//  but users may start editing launch with the start date in future, and while they make changes that date might
//  become in the past. Currently we don't handle this use case.

// TODO: split into smaller components if needed
const EditIncrementalLaunchModal: React.FC<EditIncrementalLaunchModalProps> = ({ isOpen, versionScheduling, mode, launch, onClose }) => {
  const { success, info, error } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [launches, setLaunches] = useState<FocusScheduling[] | undefined>();
  const [step, setStep] = useState(1);
  const dispatch = useAppDispatch();
  const rightsPossible = useAppSelector((state) => selectFocusRightsPossibleBySchedulingId(state, versionScheduling.id));
  const rightsAvailable = useRightsAvailable(rightsPossible);

  const initialStartDate = useRef<string | undefined>();
  const initialEndDate = useRef<string | undefined>();
  // Initial date values are set in the `useEffect`
  const [startDate, setStartDate] = useState<string | undefined>();
  const [endDate, setEndDate] = useState<string | undefined>();

  const revertDateChangeParams = useRef<{ field: DateChangeField; date: Date | null } | null>(null);
  const { dialog: dateChangeConfirmationDialog, setDialogVisible: setDateChangeConfirmationDialogVisibility } = useConfirmationPrompt({
    title: 'Changing date will reset all changes',
    message: 'Do you really want to apply new date and clear audio, timed text and territory changes?',
    buttons: {
      cancelText: 'Cancel',
      submitText: 'Clear changes'
    },
    onConfirm: () => {
      setRights(INITIAL_RIGHTS_STATE);
      revertDateChangeParams.current = null;
    },
    onCancel: () => {
      if (!revertDateChangeParams.current) {
        console.error('`revertDateChangeParams` is null. This should not happen.');
        return;
      }
      const { field, date } = revertDateChangeParams.current;
      updateDate(field, date);
      revertDateChangeParams.current = null;
    }
  });

  const [tab, setTab] = useState<ScheduleAvailType>(INITIAL_TAB);

  const isStartDateDisabled = useMemo(() => {
    // Disable the start date picker when editing an existing expansion launch that has already
    // started (window_start is in the past)
    return launch && mode === 'add' && new Date(launch.window_start).getTime() < new Date().getTime();
  }, [mode, launch]);

  const initialLaunchDate = useMemo(() => {
    if (!launches || launches.length === 0) return undefined;
    return new Date(Math.min(...launches.map((l) => new Date(l.window_start).getTime())));
  }, [launches]);

  const prevLaunch = useMemo(() => {
    if (!launch || !launches) return undefined;
    const pLaunch = getPrevScheduling({ date: launch.window_start, schedulings: launches });
    return pLaunch.id !== launch.id ? pLaunch : undefined;
  }, [launch, launches]);

  const originalRights = useMemo(() => {
    if (!isOpen || !launches) return;

    if (launch) {
      if (prevLaunch) {
        return {
          audio: launch.audio_languages.filter((l) => prevLaunch.audio_languages.includes(l)),
          timedtext: launch.timed_text_languages.filter((l) => prevLaunch.timed_text_languages.includes(l)),
          territories: launch.territory.filter((code) => prevLaunch.territory.includes(code)).map((code) => code.toUpperCase())
        };
      }
      return {
        audio: [],
        timedtext: [],
        territories: []
      };
    }

    return getAppliedRights({ date: mode === LaunchModalMode.Add ? startDate : endDate, schedulings: launches });
  }, [isOpen, mode, startDate, endDate, launch, launches, prevLaunch]);

  const [rights, setRights] = useState<Record<ScheduleAvailType, IncrementalSelectionState>>(INITIAL_RIGHTS_STATE);

  // indicates if user added or removed any rights
  const hasRightsChanged = useMemo(() => {
    return Object.values(rights)
      .map((rightsByType) => [rightsByType.add, rightsByType.remove])
      .flat()
      .some((list) => list.length > 0);
  }, [rights]);

  const hasStartDateChanged = useMemo(() => {
    // date strings stored on server might have slightly different format from date strings return by Datepicker
    // component ("2023-07-06T07:00:00Z" vs "2023-07-06T07:00:00.000Z"). That's why use .getTime() function before comparing them.
    return new Date(initialStartDate.current ?? 0).getTime() !== new Date(startDate ?? 0).getTime();
  }, [startDate]);
  const hasEndDateChanged = useMemo(() => {
    // date strings stored on server might have slightly different format from date strings return by Datepicker
    // component ("2023-07-06T07:00:00Z" vs "2023-07-06T07:00:00.000Z"). That's why use .getTime() function before comparing them.
    return new Date(initialEndDate.current ?? 0).getTime() !== new Date(endDate ?? 0).getTime();
  }, [endDate]);

  const isNextButtonDisabled = useMemo(() => {
    if (launch) {
      // When editing an existing launch: for Add mode only start date matters, for Remove only end date matters
      if (mode === LaunchModalMode.Add) return !hasStartDateChanged && !hasRightsChanged;
      return !hasEndDateChanged && !hasRightsChanged;
    } else {
      // When creating a new launch, rights must change (date changes alone are not sufficient), for a takedown date must be set
      return !hasRightsChanged || (mode === LaunchModalMode.Remove && !endDate);
    }
  }, [launch, mode, hasRightsChanged, hasStartDateChanged, hasEndDateChanged]);

  const filterStartDateMinTime = (time: Date) => {
    const now = new Date();
    const minTime = initialLaunchDate && initialLaunchDate > now ? initialLaunchDate : now;
    if (isSameDay(minTime, time)) {
      return time.getTime() >= minTime.getTime();
    }
    return true;
  };

  // This function is used to disable times in end date field that are before the start date
  const filterEndDateMinTime = (time: Date) => {
    if (!startDate) {
      return true;
    }

    const dateStartDate = new Date(startDate);

    if (!endDate || isSameDay(dateStartDate, new Date(endDate))) {
      return getTimeMs(time) > getTimeMs(dateStartDate);
    }

    return true;
  };

  const updateDate = (field: DateChangeField, date: Date | null) => {
    const newDate = date ? date.toISOString() : undefined;
    if (field === 'start') {
      setStartDate(newDate);
    } else {
      setEndDate(newDate);
    }
  };

  const handleDateChange = (field: DateChangeField, date: Date | null) => {
    updateDate(field, date);
    if (hasRightsChanged) {
      revertDateChangeParams.current = {
        field,
        date: field === 'start' ? (startDate ? new Date(startDate) : null) : endDate ? new Date(endDate) : null
      };

      setDateChangeConfirmationDialogVisibility(true);
    }
  };

  const handleRightsChange = (state: IncrementalSelectionState) => {
    if (mode === LaunchModalMode.Add && state.remove.length > 0) {
      info('You cannot remove rights when in "add" mode');
      return;
    }

    if (mode === LaunchModalMode.Remove && state.add.length > 0) {
      info('You cannot add rights when in "remove" mode');
      return;
    }

    setRights({
      ...rights,
      [tab]: state
    });
  };

  const handleSave = () => {
    if (!originalRights) {
      error('Rights not loaded');
      return;
    }

    if (!startDate) {
      error('Missing start date');
      return;
    }

    const launchRights = computeLaunchRights(rights, originalRights);

    const schedulePostData: Partial<SchedulingPostData> = {
      version_id: versionScheduling.version.id,
      customer_id: versionScheduling.customer.id,
      primary_av_lang: versionScheduling.primary_av_lang,
      platform: versionScheduling.platform,
      license: versionScheduling.license,
      window_start: new Date(startDate).toISOString(),
      window_end: endDate ? new Date(endDate).toISOString() : defaultAvailEndDate,
      ...launchRights
    };

    const endPoint = launch ? `focus/scheduling/${launch.id}/` : 'focus/scheduling/create/';
    const actionNamePast = launch ? 'updated' : 'created';
    const actionNameContinuous = launch ? 'updating' : 'creating';

    //TODO: KYLE this is temporary and should be handled by the BE. Remove this when that is the case
    updateFutureLaunches();

    axios
      .post(endPoint, schedulePostData, { baseURL: '/api/v1.1' })
      .then(() => {
        success(`Schedule ${actionNamePast} successfully`);
        dispatch(fetchIncrementalSchedulingsByVersionSchedulingId({ versionSchedulingId: versionScheduling.id }));
        onClose();
      })
      .catch((err) => {
        console.error(`Error ${actionNameContinuous} schedule:`, err);
        error('Error creating schedule');
      });
  };

  const updateFutureLaunches = () => {
    if (!launches || (launch && launches.length === 0) || !originalRights) return;
    const launchDate = new Date(mode === LaunchModalMode.Add ? (startDate ?? launch?.window_start ?? '') : (endDate ?? launch?.window_end ?? ''));
    const originalItems = launch
      ? { territories: launch.territory, audio: launch.audio_languages, timedtext: launch.timed_text_languages }
      : originalRights;
    const newItems = computeLaunchRights(rights, originalRights);
    const deltaItems = {
      territories: {
        added: newItems.territory.filter((item) => !originalItems.territories.includes(item)),
        removed: originalItems.territories.filter((item) => !newItems.territory.includes(item))
      },
      audio: {
        added: newItems.audio_languages.filter((item) => !originalItems.audio.includes(item)),
        removed: originalItems.audio.filter((item) => !newItems.audio_languages.includes(item))
      },
      timedtext: {
        added: newItems.timed_text_languages.filter((item) => !originalItems.timedtext.includes(item)),
        removed: originalItems.timedtext.filter((item) => !newItems.timed_text_languages.includes(item))
      }
    };
    const affectedLaunches = launches.filter((l) => {
      if (launch && l.id === launch.id) return false;
      return l.window_end === launches[0].window_end ? new Date(l.window_start) > launchDate : new Date(l.window_end) > launchDate;
    });

    const applyDelta = (items: string[], delta: { added: string[]; removed: string[] }) => {
      const normalized = new Set(items.map((item) => item.toLowerCase()));
      delta.added.forEach((item) => normalized.add(item.toLowerCase()));
      delta.removed.forEach((item) => normalized.delete(item.toLowerCase()));
      return Array.from(normalized);
    };

    affectedLaunches.forEach((affectedLaunch) => {
      const updatedSchedulingData: Partial<SchedulingPostData> = {
        territory: applyDelta(affectedLaunch.territory, deltaItems.territories),
        audio_languages: applyDelta(affectedLaunch.audio_languages, deltaItems.audio),
        timed_text_languages: applyDelta(affectedLaunch.timed_text_languages, deltaItems.timedtext)
      };

      axios.post(`focus/scheduling/${affectedLaunch.id}/`, updatedSchedulingData, { baseURL: '/api/v1.1' });
    });
  };

  useEffect(() => {
    if (!startDate || !endDate) return;

    // Clear the end date if it is before or equal to the start date. This can happen when the user changes the start date.
    if (new Date(startDate).getTime() >= new Date(endDate).getTime()) {
      setEndDate(undefined);
    }
  }, [startDate, endDate]);

  useEffect(
    function getPossibleRightsBySchedulingId() {
      if (isOpen) {
        dispatch(
          getFocusRightsPossibleBySchedulingId({
            schedulingId: versionScheduling.id,
            title_id: [versionScheduling.version.id],
            license: [versionScheduling.license],
            availed_platforms: [versionScheduling.platform],
            // TODO: shouldn't it be based on selected dates?
            term_start__gte: '2020-01-01T00:00:00Z',
            term_end__lt: defaultAvailEndDate
          })
        );
      }
    },
    [isOpen]
  );

  useEffect(
    function loadSchedulings() {
      if (!isOpen || launches) return;

      setIsLoading(true);
      axios
        .post<PolarisApiResponse<FocusScheduling[]>, AxiosResponse<PolarisApiResponse<FocusScheduling[]>>>(
          '/focus/scheduling/search/?page_size=999',
          {
            version_scheduling_id_filter: [versionScheduling.id]
          },
          { baseURL: '/api/v1.1' }
        )
        .then((res) => {
          const launchesSorted = res.data.data.sort((a, b) => a.id - b.id);
          setLaunches(launchesSorted);
        })
        .catch((err) => {
          console.error(err);
          error('Error loading schedules list');
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [isOpen, launches]
  );

  useEffect(
    function resetSchedulings() {
      if (!isOpen) {
        setLaunches(undefined);
      }
    },
    [isOpen]
  );

  useEffect(
    function setModalInitialState() {
      if (!isOpen || !launches?.length) return;
      const isExpansion = mode === LaunchModalMode.Add;
      const nextQuarterTime = getNextQuarterTime().toISOString();

      setStep(1);
      setTab(INITIAL_TAB);

      if (launch) {
        initialStartDate.current = launch.window_start;
        initialEndDate.current = launch.window_end;
      } else {
        if (isExpansion) {
          initialStartDate.current =
            initialLaunchDate && initialLaunchDate > new Date(nextQuarterTime) ? initialLaunchDate.toISOString() : nextQuarterTime;
        } else {
          const newTakedownStartDate = getNewTakedownStartDate(launches, rights);
          if (!newTakedownStartDate) {
            throw new Error('Could not find the date when the items we want to remove were added');
          }
          initialStartDate.current = newTakedownStartDate;
        }

        initialEndDate.current = isExpansion ? undefined : nextQuarterTime;
      }

      setStartDate(initialStartDate.current);
      setEndDate(initialEndDate.current);
      if (!launch) setRights(INITIAL_RIGHTS_STATE);

      if (launch) {
        if (!prevLaunch) {
          setRights({
            audio: { add: launch.audio_languages, remove: [] },
            timedtext: { add: launch.timed_text_languages, remove: [] },
            territories: { add: launch.territory.map((code) => code.toUpperCase()), remove: [] }
          });
        } else {
          const naudio = launch.audio_languages.filter((l) => !prevLaunch.audio_languages.includes(l));
          const ntimedtext = launch.timed_text_languages.filter((l) => !prevLaunch.timed_text_languages.includes(l));
          const nterritories = launch.territory.filter((code) => !prevLaunch.territory.includes(code)).map((code) => code.toUpperCase());
          setRights({
            audio: { add: mode === LaunchModalMode.Add ? naudio : [], remove: mode === LaunchModalMode.Remove ? naudio : [] },
            timedtext: { add: mode === LaunchModalMode.Add ? ntimedtext : [], remove: mode === LaunchModalMode.Remove ? ntimedtext : [] },
            territories: { add: mode === LaunchModalMode.Add ? nterritories : [], remove: mode === LaunchModalMode.Remove ? nterritories : [] }
          });
        }
      }
    },
    [isOpen, mode, launch, launches, prevLaunch]
  );

  const modalTitle = `${launch ? 'Edit' : 'Add'} ${mode === LaunchModalMode.Add ? 'Expansion' : 'Takedown'} Launch`;

  return (
    <>
      <StyledModal maxWidth={1440} noHeader loading={isLoading} open={isOpen} onClose={onClose} scrollDisabled title={modalTitle}>
        <StyledHeader>
          <StyledTitle variant="h2">{modalTitle}</StyledTitle>
          <StyledCloseButton onClick={onClose}>
            <StyledCloseIcon icon="close" />
          </StyledCloseButton>
        </StyledHeader>
        <StyledBody>
          <div>
            {step === 1 && (
              <>
                <Typography variant="h3" fontSize="1.143rem" fontWeight={500} mb={1}>
                  Term window
                </Typography>
                <Grid container spacing={2} mb={2}>
                  {mode === LaunchModalMode.Add && (
                    <Grid item xs={6}>
                      <Tooltip
                        slotProps={{ popper: { disablePortal: false } }}
                        title={isStartDateDisabled ? 'Cannot edit the start time of past launches' : undefined}
                        placement="top"
                      >
                        <div>
                          <Datepicker
                            label="Start"
                            minDate={initialLaunchDate && initialLaunchDate > new Date() ? initialLaunchDate : new Date()}
                            value={startDate}
                            onChange={(date) => handleDateChange('start', date)}
                            disabled={isStartDateDisabled}
                            required
                            showTimeSelect
                            timeIntervals={DATE_PICKER_TIME_INTERVALS}
                            timeCaption="Time"
                            filterTime={filterStartDateMinTime}
                          />
                        </div>
                      </Tooltip>
                    </Grid>
                  )}
                  {mode === LaunchModalMode.Remove && (
                    <Grid item xs={6}>
                      <Datepicker
                        label="End"
                        minDate={new Date(startDate ? startDate : new Date())}
                        value={endDate}
                        onChange={(date) => handleDateChange('end', date)}
                        showTimeSelect
                        timeIntervals={DATE_PICKER_TIME_INTERVALS}
                        timeCaption="Time"
                        filterTime={filterEndDateMinTime}
                        required
                      />
                    </Grid>
                  )}
                </Grid>

                <ScheduleAvailTypeTabs tab={tab} onChange={setTab} />
                <RightsSelectionWrapper>
                  {/* `originalRights` get populated only when modal is opened */}
                  {originalRights ? (
                    <IncrementalLaunchRightsSelectionSection
                      mode={mode}
                      tab={tab}
                      value={rights[tab]}
                      originalRights={originalRights[tab]}
                      onChange={handleRightsChange}
                      rightsOptions={rightsAvailable[tab]}
                    />
                  ) : (
                    <RightsSelectionPlaceholder />
                  )}
                </RightsSelectionWrapper>
              </>
            )}

            {step === 2 && originalRights && (
              <StepTwo mode={mode} startDate={startDate} endDate={endDate} rights={rights} originalRights={originalRights} />
            )}
          </div>
        </StyledBody>

        <StyledFooter>
          {step === 2 && (
            <Box flexGrow={1}>
              <StyledCancelButton color="secondary" onClick={() => setStep(1)} variant="outlined">
                Back
              </StyledCancelButton>
            </Box>
          )}

          <StyledCancelButton color="secondary" onClick={onClose} variant="outlined">
            Cancel
          </StyledCancelButton>
          {step === 1 ? (
            <Tooltip title={isNextButtonDisabled ? 'No changes have been made' : undefined}>
              <div>
                <Button color="secondary" disabled={isNextButtonDisabled} onClick={() => setStep(2)} variant="contained">
                  Next
                </Button>
              </div>
            </Tooltip>
          ) : (
            <Button color="secondary" onClick={handleSave} variant="contained">
              Save
            </Button>
          )}
        </StyledFooter>
      </StyledModal>
      {dateChangeConfirmationDialog}
    </>
  );
};

export default EditIncrementalLaunchModal;
const StyledModal = styled(Modal)`
  .modal-body {
    // make overflow visible to allow modal body's scroll (StyledBody) to be moved a bit to the right
    overflow: visible;
  }
`;

const StyledBody = styled.div`
  max-height: 75vh;
  overflow: auto;
  // negative margin and padding to move scrollbar a bit to the right (when scrollbar is visible)
  margin-right: ${({ theme }) => theme.spacing(-1.5)};
  padding-right: ${({ theme }) => theme.spacing(1.5)};
`;

const StyledCancelButton = styled(Button)`
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[40]};
  border-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[40]};
`;

const StyledCloseButton = styled(IconButton)`
  padding: 0;
`;

const StyledCloseIcon = styled(PolarisIcon)`
  font-size: 22px; // need to redefine in theme
  opacity: 0.6;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[0]};

  &:hover {
    opacity: 1;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

const RightsSelectionWrapper = styled.div`
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

const RightsSelectionPlaceholder = styled.div`
  height: ${RIGHTS_SELECTION_SECTION_HEIGHT}px;
  background: ${({ theme }) => (theme as FocusTheme).palette.neutral[90]};
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2 + 'px'};
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const StyledTitle = styled(Typography)`
  font-size: 28px; // need to redefine in theme
  font-weight: 700;
  letter-spacing: -0.2px;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[0]};
`;
